// Comments module for TipTap editors.
// Handles creating, loading, and managing inline comments.
import { buildCommentAvatar } from "./comment_avatar"

export function addCommentToMenu(bubbleEl, getContext) {
  const commentBtn = document.createElement("button")
  commentBtn.type = "button"
  commentBtn.className = "tiptap-toolbar-btn"
  commentBtn.title = "Add Comment"
  commentBtn.innerHTML = '<span class="fas fa-comment-dots"></span>'
  commentBtn.addEventListener("mousedown", (e) => {
    e.preventDefault()
    const ctx = getContext()
    if (ctx && ctx.editor) triggerAddComment(ctx.editor, ctx)
  })

  // Insert before the last element (close/link) or append
  const linkBtn = bubbleEl.querySelector('[title="Link"]') || bubbleEl.querySelector('.tiptap-toolbar-btn:last-child')
  if (linkBtn) {
    linkBtn.parentNode.insertBefore(commentBtn, linkBtn)
  } else {
    bubbleEl.appendChild(commentBtn)
  }
}

export function triggerAddComment(editor, ctx) {
  const { from, to } = editor.state.selection
  if (from === to) return // No selection

  const quoteText = editor.state.doc.textBetween(from, to, " ")
  if (quoteText.trim().length < 2) return

  const markerId = `comment-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`

  // Apply the comment highlight mark
  editor.chain()
    .focus()
    .setCommentHighlight({ "data-comment-id": markerId })
    .run()

  // Show inline comment input near the selection
  showInlineCommentInput(editor, markerId, quoteText)
}

function showInlineCommentInput(editor, markerId, quoteText) {
  // Remove any existing inline comment input
  document.querySelector(".inline-comment-input")?.remove()

  // Get position of the highlighted mark in the DOM
  const markEl = editor.view.dom.querySelector(`mark[data-comment-id="${markerId}"]`)
  const container = editor.view.dom.closest(".editor-content") || editor.view.dom.parentElement
  const rect = markEl ? markEl.getBoundingClientRect() : editor.view.coordsAtPos(editor.state.selection.from)
  const containerRect = container.getBoundingClientRect()

  const popup = document.createElement("div")
  popup.className = "inline-comment-input"
  popup.style.position = "absolute"
  popup.style.left = `${(rect.left || rect.x) - containerRect.left}px`
  popup.style.top = `${(rect.bottom || rect.y + 20) - containerRect.top + 8}px`
  popup.style.zIndex = "1100"

  popup.innerHTML = `
    <div class="inline-comment-box">
      <div class="inline-comment-quote">${escapeHtml(quoteText.substring(0, 80))}${quoteText.length > 80 ? "..." : ""}</div>
      <textarea class="inline-comment-textarea" placeholder="Add a comment..." rows="2"></textarea>
      <div class="inline-comment-actions">
        <button type="button" class="btn btn-sm btn-5 inline-comment-submit">Comment</button>
        <button type="button" class="btn btn-sm btn-2 inline-comment-cancel">Cancel</button>
      </div>
    </div>
  `

  const textarea = popup.querySelector(".inline-comment-textarea")
  const submitBtn = popup.querySelector(".inline-comment-submit")
  const cancelBtn = popup.querySelector(".inline-comment-cancel")

  let saved = false

  const cleanupOutsideListener = () => {
    document.removeEventListener("mousedown", closeOnOutside)
  }

  const closeOnOutside = (e) => {
    if (!popup.contains(e.target)) {
      popup.remove()
      if (!saved) removeCommentHighlight(editor, markerId)
      cleanupOutsideListener()
    }
  }

  submitBtn.addEventListener("click", () => {
    const body = textarea.value.trim()
    if (!body) return

    submitBtn.disabled = true
    submitBtn.textContent = "Saving..."

    // Find post ID from the page
    const postId = document.querySelector(".editor-content[data-post-id]")?.dataset.postId
      || document.querySelector("[data-comment-panel-post-id-value]")?.dataset.commentPanelPostIdValue

    if (postId) {
      saveComment({ postId, body, markerId, quoteText })
        .then(() => {
          saved = true
          cleanupOutsideListener()
          popup.remove()
          // Notify the comment panel to reload if it's open
          document.dispatchEvent(new CustomEvent("comment:created", { detail: { markerId } }))
        })
        .catch(() => {
          submitBtn.disabled = false
          submitBtn.textContent = "Comment"
        })
    }
  })

  cancelBtn.addEventListener("click", () => {
    cleanupOutsideListener()
    popup.remove()
    removeCommentHighlight(editor, markerId)
  })

  // Close on click outside (only removes highlight if comment wasn't saved)
  setTimeout(() => document.addEventListener("mousedown", closeOnOutside), 100)

  container.appendChild(popup)
  setTimeout(() => textarea.focus(), 50)
}

function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

export async function loadComments(postId) {
  const response = await fetch(`/posts/${postId}/comments`, {
    headers: { "Accept": "application/json" }
  })
  // A failed request is not an empty list: callers render the empty state only for a
  // resolved, genuinely empty response.
  if (!response.ok) throw new Error(`Comment request failed with status ${response.status}`)
  return response.json()
}

export async function saveComment({ postId, body, markerId, quoteText, postSectionId, parentId }) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  const response = await fetch(`/posts/${postId}/comments`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    body: JSON.stringify({
      body, marker_id: markerId, quote_text: quoteText,
      post_section_id: postSectionId, parent_id: parentId
    })
  })
  if (!response.ok) throw new Error("Failed to save comment")
  return response.json()
}

export async function resolveComment(postId, commentId) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  const response = await fetch(`/posts/${postId}/comments/${commentId}/resolve`, {
    method: "PATCH",
    headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken }
  })
  if (!response.ok) throw new Error("Failed to resolve comment")
  return response.json()
}

export async function deleteComment(postId, commentId) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  await fetch(`/posts/${postId}/comments/${commentId}`, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken }
  })
}

// Wire click handlers on comment highlights to show comment tooltip
export function wireCommentHighlightClicks(editorEl) {
  if (!editorEl) return
  editorEl.addEventListener("click", (e) => {
    const mark = e.target.closest("mark.comment-highlight")
    if (!mark) return

    const markerId = mark.getAttribute("data-comment-id")
    if (!markerId) return

    // Remove any existing tooltip
    document.querySelector(".comment-tooltip")?.remove()

    const postId = document.querySelector(".editor-content[data-post-id]")?.dataset.postId
    if (!postId) return

    // Fetch comment data and show tooltip
    fetch(`/posts/${postId}/comments`, { headers: { "Accept": "application/json" } })
      .then(r => r.json())
      .then(comments => {
        // Find the comment matching this marker (check roots and their replies)
        const comment = findCommentByMarker(comments, markerId)
        if (!comment) return
        showCommentTooltip(mark, comment, postId)
      })
  })
}

function findCommentByMarker(comments, markerId) {
  for (const c of comments) {
    if (c.marker_id === markerId) return c
    if (c.replies) {
      const found = findCommentByMarker(c.replies, markerId)
      if (found) return found
    }
  }
  return null
}

function showCommentTooltip(markEl, comment, postId) {
  document.querySelector(".comment-tooltip")?.remove()

  const rect = markEl.getBoundingClientRect()
  const tooltip = document.createElement("div")
  tooltip.className = "comment-tooltip"
  tooltip.style.position = "fixed"
  tooltip.style.left = `${rect.left}px`
  tooltip.style.top = `${rect.bottom + 6}px`
  tooltip.style.zIndex = "1200"

  tooltip.innerHTML = `
    <div class="comment-tooltip-box">
      <div class="comment-tooltip-header">
        <span class="comment-tooltip-author">${escapeHtml(comment.profile?.name || "Unknown")}</span>
        <span class="comment-tooltip-time">${comment.created_at ? timeAgo(comment.created_at) : ""}</span>
      </div>
      <div class="comment-tooltip-body">${escapeHtml(comment.body)}</div>
      ${comment.replies && comment.replies.length > 0 ? `<div class="comment-tooltip-replies">${comment.replies.length} ${comment.replies.length === 1 ? "reply" : "replies"}</div>` : ""}
      <div class="comment-tooltip-actions">
        <button type="button" class="comment-tooltip-btn comment-tooltip-view">View in Comments</button>
      </div>
    </div>
  `

  tooltip.querySelector(".comment-tooltip-header").prepend(buildCommentAvatar(comment.profile, "comment-tooltip-avatar"))

  // View in Comments: switch to comments panel
  tooltip.querySelector(".comment-tooltip-view")?.addEventListener("click", () => {
    tooltip.remove()
    const commentsBtn = document.getElementById("commentsItem")
    if (commentsBtn) commentsBtn.click()
  })

  // Close on click outside
  const close = (e) => {
    if (!tooltip.contains(e.target) && !markEl.contains(e.target)) {
      tooltip.remove()
      document.removeEventListener("mousedown", close)
    }
  }
  setTimeout(() => document.addEventListener("mousedown", close), 50)

  document.body.appendChild(tooltip)
}

function timeAgo(dateStr) {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMin = Math.floor((now - date) / 60000)
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return diffDay < 30 ? `${diffDay}d ago` : date.toLocaleDateString()
}

export function removeCommentHighlight(editor, markerId) {
  if (!editor) return
  const { doc } = editor.state

  doc.descendants((node, pos) => {
    if (!node.isText) return

    const mark = node.marks.find(m =>
      m.type.name === "commentHighlight" && m.attrs["data-comment-id"] === markerId
    )
    if (mark) {
      editor.chain()
        .setTextSelection({ from: pos, to: pos + node.nodeSize })
        .unsetCommentHighlight()
        .run()
    }
  })
}
